import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import Container from '@mui/material/Container';
import { useTranslation } from 'hooks';
import { Card, CardsContainer } from 'components/cards';
import { PlacementSummary, TimeTracking } from 'components/Placements';
// import MonthlyChecklist from './cards/MonthlyChecklist';
import PlacementDetails from './cards/PlacementDetails';
import { PlacementsSection } from './style';
import Notification from 'components/Notification';

function PlacementsPage({
  currentDate,
  meta,
  placementDetails,
  placements,
  allPlacements,
  syncing,
  syncingMonth,
  syncingMonthlyReport,
  onInit,
  onSaveMonthlyReport,
  onSendMonthlyReport,
  loadPlacementOverviewMonth,
  isMonthlyReportSaved,
  resetIsReportSaved,
  videoURLs,
  language
}) {
  const { placementId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const timeTrackingOptionsList = meta.timeTrackingOptions;
  const recordedTimeOptionsList = meta.recordedTimeOptions;
  const vatList = meta.vatList;

  const [toggleMonthChange, setToggleMonthChange] = useState(false);
  const [disableMouseControls, setDisableMouseControls] = useState(false);
  const [reportDate, setReportDate] = useState({
    isLoaded: false,
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });
  const [currentCompDate, setCurrentCompDate] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Initialize page
  useEffect(() => {
    onInit(placementId);
  }, [onInit, placementId]);

  // Reset monthly report saved state on first mount
  useEffect(() => {
    resetIsReportSaved && resetIsReportSaved();
  }, [resetIsReportSaved]);

  // Sync reportDate with currentDate
  useEffect(() => {
    if (
      currentDate &&
      (!currentCompDate ||
        currentDate.currentYear !== currentCompDate.currentYear ||
        currentDate.currentMonth !== currentCompDate.currentMonth)
    ) {
      setReportDate({
        year: currentDate.currentYear,
        month: currentDate.currentMonth,
        isLoaded: true
      });
      setCurrentCompDate({
        currentYear: currentDate.currentYear,
        currentMonth: currentDate.currentMonth
      });
    }
  }, [currentDate, currentCompDate]);

  const lastPlacementIdRef = React.useRef(null);

  useEffect(() => {
    if (!placementDetails || !placementDetails[placementId]) return;

    const details = placementDetails[placementId];

    // Only sync when the placementId actually changes
    if (lastPlacementIdRef.current !== placementId) {
      lastPlacementIdRef.current = placementId;

      setReportDate({
        year: details.currentYear || new Date().getFullYear(),
        month: details.currentMonth || new Date().getMonth() + 1,
        isLoaded: true
      });
    }
  }, [placementId, placementDetails]);

  const handleMonthChange = useCallback(
    date => {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      setReportDate({ year, month, isLoaded: true });
      loadPlacementOverviewMonth(placementId, year, month);
      setToggleMonthChange(!toggleMonthChange);
    },
    [placementId, loadPlacementOverviewMonth, toggleMonthChange]
  );

  const handleSelectedIndexChange = index => setSelectedIndex(index);

  // Compute placement details for rendering
  const placementDetailsData = useMemo(() => {
    if (!placementDetails || !placementDetails[placementId]) {
      const emptyData = {
        placement: {},
        currentYear: reportDate.year,
        currentMonth: reportDate.month,
        timeTrackingOptions: [],
        months: {}
      };
      emptyData.months[`${reportDate.year}_${reportDate.month}`] = {
        timeTracking: [],
        monthlyReport: {}
      };
      return emptyData;
    } else {
      const details = (placementDetails && placementDetails[placementId]) || {};
      if (!reportDate.isLoaded) {
        setReportDate({
          year: details.currentYear || new Date().getFullYear(),
          month: details.currentMonth || new Date().getMonth() + 1,
          isLoaded: true
        });
      }
      return details;
    }
  }, [placementDetails, reportDate]);

  const onSelectedPlacementChange = useCallback(
    placement => navigate(`/placement/${placement.id}`),
    [navigate]
  );

  return (
    <React.Fragment>
      {/* Top Cards Section */}
      <PlacementsSection>
        <Container>
          <Notification
            duration={4000}
            message={t('label.saveSuccess')}
            position={{ vertical: 'top', horizontal: 'center' }}
            resetGlobalState={resetIsReportSaved}
            state={isMonthlyReportSaved}
          />
          <CardsContainer>
            <Card size="4" title={t('label.dailyActivitiesProgress')} style={{ padding: '0 22px' }}>
              <TimeTracking
                disableMouseControls={disableMouseControls}
                loading={syncingMonth || syncing || syncingMonthlyReport}
                month={reportDate.month}
                year={reportDate.year}
                placementDetails={placementDetailsData}
                setDisableMouseControls={setDisableMouseControls}
                onMonthChange={handleMonthChange}
              />
            </Card>
            <Card size="7" title={t('title.placementOverview')}>
              <PlacementSummary
                allPlacements={allPlacements || []}
                loading={syncing}
                placement={placementDetailsData.placement}
                placements={placements}
                showAllPlacements
                onSelectedPlacementChange={onSelectedPlacementChange}
              />
            </Card>
            {/* Uncomment if needed */}
            {/* <MonthlyChecklist /> */}
          </CardsContainer>
        </Container>
      </PlacementsSection>

      {/* Placement Details Section */}
      <PlacementsSection>
        <Container>
          <PlacementDetails
            language={language}
            loading={syncingMonth || syncing || syncingMonthlyReport}
            month={reportDate.month}
            year={reportDate.year}
            placementDetails={placementDetailsData}
            timeTrackingOptions={timeTrackingOptionsList}
            recordedTimeOptions={recordedTimeOptionsList}
            vatList={vatList}
            videoURLs={videoURLs}
            toggleMonthChange={toggleMonthChange}
            selectedIndex={selectedIndex}
            setDisableMouseControls={setDisableMouseControls}
            onMonthChanged={handleMonthChange}
            onSelectedIndexChange={handleSelectedIndexChange}
            onSaveMonthlyReport={onSaveMonthlyReport}
            onSendMonthlyReport={onSendMonthlyReport}
          />
        </Container>
      </PlacementsSection>
    </React.Fragment>
  );
}

PlacementsPage.propTypes = {
  currentDate: PropTypes.object,
  meta: PropTypes.object.isRequired,
  placementId: PropTypes.string,
  placementDetails: PropTypes.object,
  placements: PropTypes.array,
  allPlacements: PropTypes.array,
  syncing: PropTypes.bool,
  syncingMonth: PropTypes.bool,
  syncingMonthlyReport: PropTypes.bool,
  onInit: PropTypes.func.isRequired,
  onSaveMonthlyReport: PropTypes.func.isRequired,
  onSendMonthlyReport: PropTypes.func.isRequired,
  loadPlacementOverviewMonth: PropTypes.func.isRequired,
  isMonthlyReportSaved: PropTypes.bool,
  resetIsReportSaved: PropTypes.func.isRequired,
  videoURLs: PropTypes.object,
  language: PropTypes.string
};

export default PlacementsPage;
